/**
 * Unlock Confirm Modal - Confirm spending Rumios to unlock a mystery product
 * Shows tier-specific cost (Super=3, Fresh=2, Standard=1)
 */

import { useState } from 'react';
import { X, Unlock, Loader2, AlertTriangle } from 'lucide-react';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { useUnlockProduct, useRumioBalance, useCanUnlock } from '../../hooks/useRumios';
import { UNIFIED_RUMIO_CONFIG, MYSTERY_TIERS, TIER_UNLOCK_COSTS } from '../../utils/constants';
import type { MysteryTier, DealProduct } from '../../types';

interface Props {
  product: DealProduct;
  mysteryType: MysteryTier;
  onClose: () => void;
  onSuccess: () => void;
  onNeedMore: () => void;
}

export default function UnlockConfirmModal({
  product,
  mysteryType,
  onClose,
  onSuccess,
  onNeedMore,
}: Props) {
  const { t } = useLanguage();
  const { data: balance } = useRumioBalance();
  const { mutate: unlock, isPending, isError, error } = useUnlockProduct();
  const canUnlock = useCanUnlock(mysteryType);

  const [showSuccess, setShowSuccess] = useState(false);

  const tierConfig = MYSTERY_TIERS[mysteryType];
  const unlockCost = balance?.tier_costs?.[mysteryType] ?? TIER_UNLOCK_COSTS[mysteryType];
  const currentBalance = balance?.balance ?? 0;

  const handleUnlock = () => {
    unlock(
      {
        product_code: product.product_code,
        store_id: product.store_id,
        tier: mysteryType,
      },
      {
        onSuccess: () => {
          setShowSuccess(true);
          setTimeout(() => {
            onSuccess();
          }, 1500);
        },
      }
    );
  };

  if (showSuccess) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
        <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center">
          <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4 animate-bounce">
            <Unlock className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            {t('unlockConfirm.success', 'Unlocked!')}
          </h2>
          <p className="text-gray-600">
            {t('unlockConfirm.successMessage', "You can now see this deal. It's been revealed!")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        role="button"
        tabIndex={0}
        aria-label={t('modal.close', 'Close modal')}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <div
            className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 ${tierConfig.bgColor}`}
          >
            <span className="text-3xl">{tierConfig.icon}</span>
          </div>
          <h2 className="text-xl font-bold text-gray-900">
            {t('unlockConfirm.title', 'Unlock Mystery Deal?')}
          </h2>
          <p className="text-gray-600 mt-1">
            {t('unlockConfirm.subtitle', 'Spend {cost} Rumios to reveal this {tier} deal')
              .replace('{cost}', String(unlockCost))
              .replace('{tier}', tierConfig.name)}
          </p>
        </div>

        {/* Product Preview (blurred) */}
        <div className="bg-gray-100 rounded-xl p-4 mb-6 text-center">
          <div className="w-16 h-16 mx-auto bg-gray-200 rounded-lg mb-2 flex items-center justify-center">
            <span className="text-3xl">?</span>
          </div>
          <p className="font-medium text-gray-700">
            {tierConfig.name} {t('unlockConfirm.mysteryDeal', 'Mystery Deal')}
          </p>
          <p className="text-xs text-gray-500">{tierConfig.ageRange}</p>
          {product.store_name && (
            <p className="text-xs text-gray-400 mt-1">@ {product.store_name}</p>
          )}
        </div>

        {/* Balance */}
        <div className="flex items-center justify-between bg-amber-50 rounded-lg px-4 py-3 mb-4 border border-amber-200">
          <span className="text-sm text-gray-600">
            {t('unlockConfirm.yourBalance', 'Your Rumios')}
          </span>
          <span className="font-bold text-amber-700 flex items-center gap-1">
            <img src={UNIFIED_RUMIO_CONFIG.iconUrl} alt="" className="w-5 h-5" />
            {currentBalance}
          </span>
        </div>

        {/* Cost */}
        <div className="flex items-center justify-between px-4 py-2 mb-6">
          <span className="text-sm text-gray-600">{t('unlockConfirm.cost', 'Cost')}</span>
          <span className="font-bold text-gray-900 flex items-center gap-1">
            <span>-{unlockCost}</span>
            <img src={UNIFIED_RUMIO_CONFIG.iconUrl} alt="" className="w-5 h-5" />
          </span>
        </div>

        {/* Error */}
        {isError && (
          <div className="flex items-center gap-2 text-red-600 bg-red-50 rounded-lg px-4 py-3 mb-4">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <p className="text-sm">
              {(error as Error)?.message || t('unlockConfirm.error', 'Failed to unlock. Please try again.')}
            </p>
          </div>
        )}

        {/* Actions */}
        {canUnlock ? (
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-colors"
            >
              {t('unlockConfirm.cancel', 'Cancel')}
            </button>
            <button
              onClick={handleUnlock}
              disabled={isPending}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white font-bold rounded-xl hover:from-amber-600 hover:to-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isPending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Unlock className="w-4 h-4" />
                  {t('unlockConfirm.unlock', 'Unlock')}
                </>
              )}
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-center text-sm text-amber-600 bg-amber-50 rounded-lg px-4 py-3 border border-amber-200">
              {t('unlockConfirm.notEnough', 'You need {cost} Rumios to unlock this deal').replace(
                '{cost}',
                String(unlockCost)
              )}
            </div>
            <button
              onClick={onNeedMore}
              className="w-full px-4 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white font-bold rounded-xl hover:from-amber-600 hover:to-amber-700 transition-colors"
            >
              {t('unlockConfirm.getRumios', 'Get More Rumios')}
            </button>
            <button
              onClick={onClose}
              className="w-full px-4 py-2 text-gray-500 text-sm hover:text-gray-700 transition-colors"
            >
              {t('unlockConfirm.maybeLater', 'Maybe later')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
